// Story 3.6 — TanStack Query mutation hook for POST /api/v1/vault/sealed-dossiers/:id/open.
//
// FIRST consumer of Story 3.2's reveal-orchestrator endpoint + FIRST
// `Idempotency-Key` consumer of the `/api/v1/vault/` namespace. Mirrors
// the Story 1.16 useTopUpIntent.ts pattern: caller generates a per-attempt
// idempotency key (NOT per modal mount — retries reuse the key so the
// middleware caches the response + the server-side reveal stays a one-time
// transaction).
//
// Story 3.8 EXTENSION (additive): onSuccess ALSO invalidates ["account-me"]
// so useEffectiveCeremonyMode picks up the freshly-incremented
// users.dossiers_opened_count (Story 3.2's reveal transaction bumps it
// inside the same DB transaction). Without this, a user who just opened
// their 10th dossier would still see "full" mode on their 11th — the
// cached AccountMe would lag one reveal behind. One extra GET /me per
// reveal; negligible at expected volume.
//
// Story 3.9 EXTENSION (additive): onSuccess ALSO invalidates
// ["welcome-dossier"] so the /welcome page's state flips from
// "available" → "opened" right after the user opens their welcome
// dossier. Without this, the Story 3.9 WelcomePage would still show
// "Open your first dossier" after the ceremony completes; the cached
// useWelcomeDossier would lag one reveal behind. Pure additive change.
//
// Story 4.2 EXTENSION (additive): onSuccess ALSO invalidates
// ["vault-items"] so the /vault page's grid flips the just-opened item
// from sealed → opened without a manual page refresh. The invalidation
// is partial-key — it matches every cache entry whose key starts with
// ["vault-items", ...], so all filter/sort/pagination variants the user
// may have flipped through are refreshed together.
//
// Story 4.3 EXTENSION (additive): onSuccess ALSO invalidates
// ["share-detail"] (partial-key match) so every cached share-detail
// page entry refreshes after a reveal. Useful when the user opens a
// dossier inside the welcome flow that subsequently lands on a
// share-detail page (the page should reflect the latest signed
// snapshot_image_signed_url + opened_at timestamps).
//
// Story 4.4 EXTENSION (docs only — no code change): the freshly issued
// ShareDto now carries `is_highlighted_by_owner` + `is_favourited_by_owner`
// fields from Story 4.4's ShareDto extension. Both default to `false`
// per migration 0029 L41-L45 — reveal_orchestrator hard-codes them
// to false on the just-INSERTed share. Means the Highlight + Favourite
// Switches on a newly-revealed share's share-detail page (Story 4.4)
// start unchecked, which is the correct first-time UX (the user has
// not yet opted into peacocking).
//
// Story 3.9 (welcome page) + Story 4.x (vault grid click) consume the same
// hook — single source of truth for the reveal mutation.
//
// Error handling: the hook propagates the typed error object as the
// useMutation `error` field. The CONSUMER (vault grid / welcome page)
// decides UX behavior:
//   * 409 SHARE_ALREADY_OPENED → toast + navigate to existing share.
//   * 422 SHARE_REVEAL_* (admin-config errors) → toast.error(message);
//     cancel the ceremony open.
//   * 404 SHARE_SEALED_DOSSIER_NOT_FOUND → toast (enumeration defense).
//   * Network / 5xx → toast + retry button (idempotency-key reuse
//     means server-side reveal is safe to retry).

import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { CertificateShare } from "@greenhold/components";

import { attachRequestIdToScope } from "../../lib/api-client";

export interface RevealVariables {
  sealedDossierId: string;
  idempotencyKey: string;
}

export interface RevealError {
  code: string;
  message: string;
  /** 409 SHARE_ALREADY_OPENED payload: the existing share's open
   *  timestamp so the consumer can show "opened 3 minutes ago". */
  opened_at?: string;
  /** 409 SHARE_ALREADY_OPENED payload: the existing share id so the
   *  consumer can navigate the user to the share-detail page. */
  existing_share_id?: string;
}

async function postReveal(
  sealedDossierId: string,
  idempotencyKey: string,
): Promise<CertificateShare> {
  const response = await fetch(
    "/api/v1/vault/sealed-dossiers/" + sealedDossierId + "/open",
    {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        "x-idempotency-key": idempotencyKey,
      },
      body: "{}",
    },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: RevealError = {
      code: "REVEAL_FAILED",
      message: "Reveal failed (HTTP " + response.status + ")",
    };
    try {
      const body = await response.json();
      if (body?.error) detail = body.error;
    } catch {
      // Body not JSON — keep the default error shape.
    }
    throw detail;
  }
  const body = (await response.json()) as { data: CertificateShare };
  return body.data;
}

export function useReveal() {
  const queryClient = useQueryClient();
  return useMutation<CertificateShare, RevealError, RevealVariables>({
    mutationFn: ({ sealedDossierId, idempotencyKey }) =>
      postReveal(sealedDossierId, idempotencyKey),
    onSuccess: () => {
      // Story 4.x's vault page will register this query key. The
      // invalidate is a harmless no-op at v1 (no listener yet) +
      // automatically refreshes the vault grid once it ships.
      queryClient.invalidateQueries({ queryKey: ["vault-shares"] });
      // Story 3.8 — re-fetch the AccountMe envelope so the freshly
      // bumped users.dossiers_opened_count is visible to the next
      // useEffectiveCeremonyMode resolution (graduation eligibility).
      queryClient.invalidateQueries({ queryKey: ["account-me"] });
      // Migration 0047 — the welcome dossier is no longer opened from a
      // dedicated /welcome page; the weekly free-claim status (["free-claim"])
      // tracks whether the user CLAIMED this window, which opening a dossier
      // does not change — so no free-claim invalidation is needed here.
      // Story 4.2 — re-fetch the vault grid so the just-opened item
      // flips from sealed → opened across every filter/sort variant
      // the user may have cached.
      queryClient.invalidateQueries({ queryKey: ["vault-items"] });
      // Story 4.3 — re-fetch every cached share-detail page entry
      // (partial-key match) so signed_url + opened_at reflect the
      // latest reveal-flow output.
      queryClient.invalidateQueries({ queryKey: ["share-detail"] });
    },
  });
}
